import React, { useState, useEffect } from "react";
import { Search, Filter, ArrowLeft, Send, MoreHorizontal } from "lucide-react";
import { Button } from "./ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "./ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { ScrollArea } from "./ui/scroll-area";
import { ApiResponse } from "../types/common";
import { chatRelatedApi } from "../utils/api";

interface ChatConversation {
  id: string;
  coachId: string;
  coachName: string;
  coachUsername: string;
  athleteId: string;
  athleteName: string;
  athleteUsername: string;
  lastMessage: string;
  lastMessageTime: string;
  messageCount: number;
}

interface ChatMessage {
  id: string;
  senderId: string;
  senderName: string;
  senderType: "COACH" | "ATHLETE";
  message: string;
  timestamp: string;
  read: boolean;
}

export function ChatMessages() {
  const [conversations, setConversations] = useState<ChatConversation[]>([]);
  const [filteredConversations, setFilteredConversations] = useState<ChatConversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<ChatConversation | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentView, setCurrentView] = useState<"list" | "chat">("list");

  // Filter states
  const [searchCoach, setSearchCoach] = useState("");
  const [searchAthlete, setSearchAthlete] = useState("");

  // Simulate API call to fetch conversations
  useEffect(() => {
    const fetchConversations = async () => {
      setIsLoading(true);
      try {
        
        const response: ApiResponse = await chatRelatedApi.getAllChats();
        
        setConversations(response.data.chats || []);
        setFilteredConversations(response.data.chats);
      } catch (error) {
        console.error("Failed to fetch conversations:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (currentView === "list") {
      fetchConversations();
    }
  }, [currentView]);

  // Apply filters
  useEffect(() => {
    let filtered = [...conversations];

    // Coach search
    if (searchCoach) {
      filtered = filtered.filter(conv => 
        conv.coachName.toLowerCase().includes(searchCoach.toLowerCase()) ||
        conv.coachUsername.toLowerCase().includes(searchCoach.toLowerCase())
      );
    }

    // Athlete search
    if (searchAthlete) {
      filtered = filtered.filter(conv => 
        conv.athleteName.toLowerCase().includes(searchAthlete.toLowerCase()) ||
        conv.athleteUsername.toLowerCase().includes(searchAthlete.toLowerCase())
      );
    }

    setFilteredConversations(filtered);
  }, [conversations, searchCoach, searchAthlete]);

  const clearFilters = () => {
    setSearchCoach("");
    setSearchAthlete("");
  };

  const handleConversationClick = async (conversation: ChatConversation) => {
    setSelectedConversation(conversation);
    setCurrentView("chat");
    // Simulate API call to fetch messages for this conversation
    try {
      const response: ApiResponse = await chatRelatedApi.getById(conversation.id);
        
      setMessages(response.data || []);
    } catch (error) {
      console.error("Failed to fetch messages:", error);
    }
  };

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    
    if (hours < 1) {
      const minutes = Math.floor(diff / (1000 * 60));
      return `${minutes}m ago`;
    } else if (hours < 24) {
      return `${hours}h ago`;
    } else {
      const days = Math.floor(hours / 24);
      return `${days}d ago`;
    }
  };

  if (currentView === "chat" && selectedConversation) {
    return (
      <div className="flex flex-col h-full">
        {/* Chat Header */}
        <div className="p-6 border-b">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => {
                setCurrentView("list");
                setSelectedConversation(null);
                setMessages([]);
              }}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to List
            </Button>
            <div className="flex items-center gap-3">
              <Avatar className="h-10 w-10">
                <AvatarImage src="" />
                <AvatarFallback>
                  {selectedConversation.coachName.split(' ').map(n => n[0]).join('')}
                </AvatarFallback>
              </Avatar>
              <div>
                <h3>{selectedConversation.coachName} ↔ {selectedConversation.athleteName}</h3>
                <p className="text-sm text-muted-foreground">
                  Coach: @{selectedConversation.coachUsername} • Athlete: @{selectedConversation.athleteUsername}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Chat Messages */}
        <div className="flex-1 flex flex-col">
          <ScrollArea className="flex-1 p-6">
            <div className="space-y-4">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex ${message.senderType === "COACH" ? "justify-start" : "justify-end"}`}
                >
                  <div className={`max-w-[70%] ${
                    message.senderType === "COACH" 
                      ? "bg-muted text-foreground" 
                      : "bg-primary text-primary-foreground"
                  } rounded-lg p-3`}>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs opacity-70">{message.senderName}</span>
                      <span className="text-xs opacity-50">{formatTime(message.timestamp)}</span>
                    </div>
                    <p className="text-sm">{message.message}</p>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>

          {/* Message Input */}
          <div className="p-6 border-t">
            <div className="flex gap-2">
              <Input
                placeholder="Type a message..."
                className="flex-1"
              />
              <Button size="sm">
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="space-y-4">
          <div className="h-8 bg-muted animate-pulse rounded" />
          <div className="h-4 bg-muted animate-pulse rounded w-1/3" />
          <div className="space-y-2">
            {Array.from({ length: 10 }, (_, i) => (
              <div key={i} className="h-12 bg-muted animate-pulse rounded" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1>Chat Messages</h1>
          <p className="text-muted-foreground">
            View and manage conversations between coaches and athletes
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm bg-primary text-primary-foreground px-2 py-1 rounded">
            {filteredConversations.length}
          </span>
          <Button variant="outline">
            <Filter className="h-4 w-4 mr-2" />
            Filter
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Conversation List</CardTitle>
              <CardDescription>
                Filter and search through chat conversations
              </CardDescription>
            </div>
            <Button variant="outline" onClick={clearFilters}>
              Clear Filters
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="text-sm mb-2 block">Search Coach</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by coach name or username..."
                  value={searchCoach}
                  onChange={(e) => setSearchCoach(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            <div>
              <label className="text-sm mb-2 block">Search Athlete</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by athlete name or username..."
                  value={searchAthlete}
                  onChange={(e) => setSearchAthlete(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
          </div>

          {/* Results Count */}
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-muted-foreground">
              Showing {filteredConversations.length} of {conversations.length} conversations
            </p>
          </div>

          {/* Conversations Table */}
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Coach Id</TableHead>
                  <TableHead>Athlete Id</TableHead>
                  <TableHead>Last Message</TableHead>
                  <TableHead>Message Count</TableHead>
                  <TableHead>Last Active</TableHead>
                  <TableHead className="w-[50px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredConversations.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8">
                      <div className="text-muted-foreground">
                        <Search className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        <p>No conversations found matching your filters</p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredConversations.map((conversation) => (
                    <TableRow 
                      key={conversation.id} 
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => handleConversationClick(conversation)}
                    >
                      <TableCell>
                        <div>
                          <div>{conversation.coachName}</div>
                          <div className="text-sm text-muted-foreground">@{conversation.coachUsername}</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <div>{conversation.athleteName}</div>
                          <div className="text-sm text-muted-foreground">@{conversation.athleteUsername}</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="max-w-[200px] truncate" title={conversation.lastMessage}>
                          {conversation.lastMessage}
                        </div>
                      </TableCell>
                      <TableCell>{conversation.messageCount}</TableCell>
                      <TableCell>{formatTime(conversation.lastMessageTime)}</TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" onClick={(e) => e.stopPropagation()}>
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={(e) => {
                              e.stopPropagation();
                              handleConversationClick(conversation);
                            }}>
                              View Conversation
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={(e) => e.stopPropagation()}>
                              Export Messages
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={(e) => e.stopPropagation()}
                              className="text-destructive"
                            >
                              Delete Conversation
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}